/**
 * THE COMPONENT-STRING CATALOGUE — declared keys, because there is no tree to derive them from.
 * ===========================================================================================
 *
 * `keys.ts` argues at length that schema keys must be **derived** rather than declared, and that
 * argument still holds: 700 hand-written keys is 700 chances to attach a question's text to a
 * different question. Component strings have no schema tree to take an address from, so this file is
 * the exception, and it pays the price the derived design avoids:
 *
 *   − A key can be duplicated. Guarded by {@link UI_DUPLICATE_KEYS}, which is asserted empty.
 *   − A key can be mistyped at the call site. Guarded by the exported key constants: `validate.ts`
 *     writes `VALIDATION_KEYS.requiredAnswer`, not a string literal, so a typo is a type error.
 *   − A key can be orphaned. Guarded by tests that drive the real code paths and compare the keys they
 *     actually emit against the keys declared here, **in both directions**.
 *   − The English can go missing, which for a derived key is impossible because the English is
 *     physically in the schema. Guarded structurally instead: the English lives in this file as the
 *     `pattern` of the entry, and there is no code path that reads a `ui.*` key without reading its
 *     English at the same moment (see `resolve.ts`). A missing translation renders that English; there
 *     is nothing to lose that is not also the thing being looked up.
 *
 * ─── WHY THESE THREE FILES FIRST ────────────────────────────────────────────────────────────────
 *
 * `validate.ts` is here because its messages are **the only remaining strings that block a technician
 * from submitting**. English there is not an inconvenience: it is a Spanish technician stopped by a
 * sentence they cannot read with no way forward. `core/storage.ts` is here because its four verdicts
 * are the honesty guarantee about whether queued work can be deleted. `compose/ApiNotice.tsx` is here
 * because it is the largest single body of component text and it was unclaimed.
 *
 * ─── WHAT IS STILL ENGLISH INSIDE A TRANSLATED MESSAGE, STATED PLAINLY ──────────────────────────
 *
 * Two things, both deliberate, both recorded in the entry notes:
 *
 *  1. **`{what}` in the notice bodies is an English clause supplied by the caller** —
 *     `compose/screens.tsx` passes `"look up that kiosk"` / `"read your work list"`. Those literals
 *     live in a file this work does not own, so a Spanish notice will read Spanish around an English
 *     verb phrase. That is a visible, honest partial state, and it is better than either editing a
 *     file another agent holds or inventing a second copy of those phrases here that could drift from
 *     the ones actually passed in.
 *  2. **`{blocker}`, HTTP statuses and request ids are never translated.** They are identifiers a
 *     technician reads aloud to support. `PRD-0.12-salesforce-identity` must be the same fourteen
 *     characters in every locale or the person on the other end of the phone cannot find it — the same
 *     rule `format.ts` applies to `HH:MM`.
 *
 * ⚠️ **NO TRANSLATIONS ARE PRODUCED HERE.** Every pattern below is authored English. The non-English
 * catalogues in `catalog/` remain empty and `awaiting-translation`, so every one of these strings
 * renders visibly in English until a professional localiser fills them. A machine-translated blocking
 * validation message is worse than an English one: the technician who cannot read English at least
 * knows they cannot read it.
 */

import { hashSource } from './keys.ts';
import {
  canonicalSource,
  declaredCategories,
  isPluralPattern,
  placeholdersIn,
} from './message.ts';
import type { MessagePattern, PluralCategory } from './message.ts';
import type { SourceRecord } from './resolve.ts';

/** Which part of the app a string belongs to. Lets a translator see what they are translating. */
export type UiRole = 'validation' | 'storage-verdict' | 'notice-title' | 'notice-body' | 'notice-label';

/** The top-level namespace of a `ui.*` key. Used for per-namespace anti-vacuity floors. */
export type UiNamespace = 'validate' | 'storage' | 'api';

/* ───────────────────────────────────────────────────────────── the key constants ─────────────── */

/**
 * Keys for `src/forms/validate.ts`. **The blocking set.**
 *
 * Referenced by constant rather than by literal at every call site, so a mistyped key is a compile
 * error instead of a message that resolves to `unknown-key` and renders as `ui.validate.reqiured`.
 */
export const VALIDATION_KEYS = {
  requiredAnswer: 'ui.validate.required.answer',
  requiredPhoto: 'ui.validate.required.photo',
  requiredSerial: 'ui.validate.required.serial',
  photoMin: 'ui.validate.photo.min',
  photoMax: 'ui.validate.photo.max',
  choiceMin: 'ui.validate.choice.min',
  choiceMax: 'ui.validate.choice.max',
  notAnOption: 'ui.validate.choice.not-an-option',
  notANumber: 'ui.validate.number.not-a-number',
  notWhole: 'ui.validate.number.not-whole',
  numberMin: 'ui.validate.number.min',
  numberMax: 'ui.validate.number.max',
  textMinLength: 'ui.validate.text.min-length',
  textMaxLength: 'ui.validate.text.max-length',
  patternDefault: 'ui.validate.pattern.default',
} as const;

/** Keys for `src/core/storage.ts`. Four verdicts, exactly one of them reassuring. */
export const STORAGE_KEYS = {
  persisted: 'ui.storage.persisted',
  notPersisted: 'ui.storage.not-persisted',
  unsupported: 'ui.storage.unsupported',
  error: 'ui.storage.error',
} as const;

/** Keys for `src/compose/ApiNotice.tsx`. One title and one body per outcome, plus the quote label. */
export const NOTICE_KEYS = {
  okTitle: 'ui.api.ok.title',
  okBody: 'ui.api.ok.body',
  offlineTitle: 'ui.api.offline.title',
  offlineBody: 'ui.api.offline.body',
  networkErrorTitle: 'ui.api.network-error.title',
  networkErrorBody: 'ui.api.network-error.body',
  notBuiltYetTitle: 'ui.api.not-built-yet.title',
  notBuiltYetBody: 'ui.api.not-built-yet.body',
  serverErrorTitle: 'ui.api.server-error.title',
  serverErrorBody: 'ui.api.server-error.body',
  notFoundTitle: 'ui.api.not-found.title',
  notFoundBody: 'ui.api.not-found.body',
  rejectedTitle: 'ui.api.rejected.title',
  rejectedBody: 'ui.api.rejected.body',
  conflictTitle: 'ui.api.conflict.title',
  conflictBody: 'ui.api.conflict.body',
  unauthenticatedTitle: 'ui.api.unauthenticated.title',
  unauthenticatedBody: 'ui.api.unauthenticated.body',
  authNotConfiguredTitle: 'ui.api.auth-not-configured.title',
  authNotConfiguredBody: 'ui.api.auth-not-configured.body',
  quoteLabel: 'ui.api.quote.label',
} as const;

export type ValidationKey = (typeof VALIDATION_KEYS)[keyof typeof VALIDATION_KEYS];
export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];
export type NoticeKey = (typeof NOTICE_KEYS)[keyof typeof NOTICE_KEYS];
export type UiKey = ValidationKey | StorageKey | NoticeKey;

/* ─────────────────────────────────────────────────────── translator notes, reused ───────────── */

const NOTE_COUNT =
  'PLURAL. `{count}` is a whole number. Supply the forms your language actually uses — a locale with a ' +
  '`many` or `few` category will otherwise fall back to `other`, which is grammatical in English and ' +
  'wrong in Spanish, Italian, French and Portuguese for large numbers. Japanese needs one form only.';

const NOTE_BOUND =
  'The number is a limit the technician has to satisfy BY TYPING, so it is interpolated with the digits ' +
  'exactly as authored and is NOT locale-formatted. Do not add a grouping separator in the translation ' +
  'either: "3.000" invites a German technician to type a value that parses as 3.';

const NOTE_GROUPED_COUNT =
  '`{count}` is a quantity the technician only READS (never types back), so it arrives already formatted ' +
  'for your locale by `Intl.NumberFormat` — under de-DE a limit of 20000 renders as "20.000". Leave the ' +
  'token alone and do not add digits or separators of your own around it.';

const NOTE_PICKLIST_VALUES =
  'The `{values}` list is one or more Salesforce picklist VALUES, already joined for your locale by ' +
  '`Intl.ListFormat` (de: "Alpha und Beta"). The VALUES themselves are submitted verbatim and several ' +
  'land on restricted picklists, so they are deliberately NOT translated — the technician has to match ' +
  'what the option actually says. Translate the sentence around them and leave the token alone.';

const NOTE_WHAT =
  'BLOCKED ON A FILE THIS WORK DOES NOT OWN: `{what}` is an English verb phrase supplied by the caller ' +
  '(`compose/screens.tsx` passes "look up that kiosk" / "read your work list"). Until those literals are ' +
  'externalised too, this sentence will read as your language wrapped around an English clause. Write the ' +
  'sentence so that reads as tolerably as possible; do not try to compensate by rewording the meaning.';

const NOTE_IDENTIFIER =
  'NEVER TRANSLATE the interpolated identifier. It is read aloud to support, who search for it verbatim.';

const NOTE_BLOCKING =
  'BLOCKS SUBMISSION. A technician who cannot read this sentence cannot finish the job and has no way ' +
  'forward. Accuracy over brevity; if the target language needs more words, use more words.';

const NOTE_STORAGE_REFUSAL =
  'SAFETY WORDING, NOT UI CHROME. This is the browser REFUSING to protect queued work, and the ' +
  'technician has to understand that a submission can be deleted by the phone. Do NOT soften it to ' +
  '"storage may be limited" or any equivalent hedge — the module exists to stop a refusal being rounded ' +
  'up to a yes, and a gentle translation reintroduces exactly that defect. Keep the emphasis on "did NOT ' +
  'agree" however your language carries emphasis.';

/* ─────────────────────────────────────────────────────────────── the strings ────────────────── */

export interface UiStringDef {
  readonly key: UiKey;
  /** The authored English. A `PluralForms` object where the sentence counts something. */
  readonly pattern: MessagePattern;
  readonly role: UiRole;
  readonly notes?: string;
}

function def(key: UiKey, pattern: MessagePattern, role: UiRole, notes?: string): UiStringDef {
  return { key, pattern, role, ...(notes !== undefined ? { notes } : {}) };
}

/**
 * The catalogue. **Every string here was moved from a component verbatim**, with one deliberate
 * exception noted on `photoMin`/`photoMax`.
 */
export const UI_STRINGS: readonly UiStringDef[] = [
  /* ── validate.ts: the only strings that stop a submission ──────────────────────────────────── */
  def(VALIDATION_KEYS.requiredAnswer, 'An answer is required', 'validation', NOTE_BLOCKING),
  def(VALIDATION_KEYS.requiredPhoto, 'At least one photo is required', 'validation', NOTE_BLOCKING),
  def(VALIDATION_KEYS.requiredSerial, 'A serial number is required', 'validation', NOTE_BLOCKING),
  /*
   * THE ONE WORDING CHANGE IN THIS FILE, and it is a bug fix. The English was
   * `At least ${minPhotos} photos are needed` unconditionally, so a field with `minPhotos: 1` told the
   * technician "At least 1 photos are needed". That is the `n === 1` problem in its simplest form and
   * it was already wrong in the source language.
   */
  def(
    VALIDATION_KEYS.photoMin,
    { one: 'At least {count} photo is needed', other: 'At least {count} photos are needed' },
    'validation',
    `${NOTE_BLOCKING} ${NOTE_COUNT} ${NOTE_GROUPED_COUNT}`,
  ),
  def(
    VALIDATION_KEYS.photoMax,
    { one: 'No more than {count} photo', other: 'No more than {count} photos' },
    'validation',
    `${NOTE_BLOCKING} ${NOTE_COUNT} ${NOTE_GROUPED_COUNT}`,
  ),
  /*
   * `choiceMin`/`choiceMax` are NOT plural patterns, and that is a judgement rather than an oversight:
   * the English has no countable noun to inflect ("Choose at least 2"), so declaring `one`/`other`
   * forms would mean two identical English sentences — and an identical pair is an invitation for a
   * translator to differentiate them by accident. A language that does need to inflect here can supply
   * `forms` in its catalogue; `resolve.ts` renders them, because the plural machinery keys off the
   * TRANSLATION's forms as well as the source's.
   */
  def(VALIDATION_KEYS.choiceMin, 'Choose at least {count}', 'validation', `${NOTE_BLOCKING} ${NOTE_GROUPED_COUNT}`),
  def(VALIDATION_KEYS.choiceMax, 'Choose no more than {count}', 'validation', `${NOTE_BLOCKING} ${NOTE_GROUPED_COUNT}`),
  def(VALIDATION_KEYS.notAnOption, 'Not an option: {values}', 'validation', `${NOTE_BLOCKING} ${NOTE_PICKLIST_VALUES}`),
  def(VALIDATION_KEYS.notANumber, 'Must be a number', 'validation', NOTE_BLOCKING),
  def(VALIDATION_KEYS.notWhole, 'Must be a whole number', 'validation', NOTE_BLOCKING),
  def(VALIDATION_KEYS.numberMin, 'Must be at least {min}', 'validation', `${NOTE_BLOCKING} ${NOTE_BOUND}`),
  def(VALIDATION_KEYS.numberMax, 'Must be at most {max}', 'validation', `${NOTE_BLOCKING} ${NOTE_BOUND}`),
  def(
    VALIDATION_KEYS.textMinLength,
    { one: 'Must be at least {count} character', other: 'Must be at least {count} characters' },
    'validation',
    `${NOTE_BLOCKING} ${NOTE_COUNT} ${NOTE_GROUPED_COUNT}`,
  ),
  def(
    VALIDATION_KEYS.textMaxLength,
    { one: 'Must be at most {count} character', other: 'Must be at most {count} characters' },
    'validation',
    `${NOTE_BLOCKING} ${NOTE_COUNT} ${NOTE_GROUPED_COUNT}`,
  ),
  def(VALIDATION_KEYS.patternDefault, 'Not in the expected format', 'validation', NOTE_BLOCKING),

  /* ── storage.ts: four verdicts, and only one of them is good news ──────────────────────────── */
  /*
   * THE FOUR MUST STAY MUTUALLY DISTINGUISHABLE IN EVERY LOCALE. `persisted` is the ONLY reassuring
   * one. `not-persisted` (we asked, the phone said no) and `unsupported` (we could not ask) are
   * different facts about different people's problems and must not converge on one hedge in
   * translation. A test asserts all four differ and that exactly one contains the reassurance.
   */
  def(
    STORAGE_KEYS.persisted,
    'Saved work is protected from being cleared automatically.',
    'storage-verdict',
    'THE ONLY REASSURING VERDICT of the four. Do not let any of the other three read like this one. ' +
      'Note it does NOT promise the work survives the user clearing site data or deleting the app, and ' +
      'the translation must not upgrade it into a broader promise.',
  ),
  def(
    STORAGE_KEYS.notPersisted,
    'This phone did NOT agree to protect saved work. Anything still waiting to send can be deleted ' +
      'by the phone if you do not open the app for a while. Send your work as soon as you have signal.',
    'storage-verdict',
    NOTE_STORAGE_REFUSAL,
  ),
  def(
    STORAGE_KEYS.unsupported,
    'This browser will not promise to keep saved work. Send your submissions before closing the app — ' +
      'the phone may delete anything still waiting.',
    'storage-verdict',
    'DIFFERENT FROM THE REFUSAL ABOVE: here the browser could not even be asked, so nothing is known. ' +
      'Keep the two distinguishable — "the browser has no way to promise" versus "the phone said no". ' +
      'Both are treated as unsafe, but they are reported to different people.',
  ),
  def(
    STORAGE_KEYS.error,
    'We could not check whether this phone will keep saved work. Treat it as unsafe: send your ' +
      'work as soon as you have signal.',
    'storage-verdict',
    'The check itself failed. "Treat it as unsafe" is the operative instruction and must survive ' +
      'translation as an instruction, not as a caveat.',
  ),

  /* ── ApiNotice.tsx: one outcome, one message, and `not-built-yet` is not "you are offline" ─── */
  def(NOTICE_KEYS.okTitle, 'Done', 'notice-title'),
  def(NOTICE_KEYS.okBody, 'The server accepted the request to {what}.', 'notice-body', NOTE_WHAT),
  def(NOTICE_KEYS.offlineTitle, 'No connection — this is not a fault', 'notice-title',
    'The "not a fault" half is the point: being offline is the normal state this app is built for, and ' +
      'the title must not read as an error.'),
  def(
    NOTICE_KEYS.offlineBody,
    'The phone has no network, so it did not try to {what}. You can keep working: anything you ' +
      'submit is saved on this phone and sent by itself when you get signal.',
    'notice-body',
    `${NOTE_WHAT} MUST NOT SHARE A SENTENCE WITH ui.api.not-built-yet.body: this one says the ` +
      'connection IS the problem, that one says it is not.',
  ),
  def(
    NOTICE_KEYS.networkErrorTitle,
    'The phone has a network but could not reach the server',
    'notice-title',
    'A third state, between "offline" and "the server answered". Keep it clearly not either of them.',
  ),
  def(
    NOTICE_KEYS.networkErrorBody,
    'That is different from being offline. It usually means a wifi that needs a login page, a ' +
      'VPN, or a connection that dropped mid-request. Moving to mobile data often fixes it. Your ' +
      'saved work is not affected.',
    'notice-body',
    'MUST NOT SHARE A SENTENCE WITH ui.api.not-built-yet.body. Conflating a network failure with an ' +
      'unbuilt server feature hides real breakage behind "you are offline", which is the specific ' +
      'defect `ApiNotice.tsx` exists to prevent. A test asserts the two messages share no sentence.',
  ),
  def(
    NOTICE_KEYS.notBuiltYetTitle,
    'This part of the server is not finished yet',
    'notice-title',
    'NOT a connection problem and NOT a retryable error. The title must say "unfinished", not "failed".',
  ),
  def(
    NOTICE_KEYS.notBuiltYetBody,
    'Nothing is wrong with your phone or your signal. The server answered that it cannot {what} ' +
      'because a dependency is still being built: {blocker}. Trying again will not help — ' +
      'it needs to be finished on our side. Report the name above if you need this today.',
    'notice-body',
    `${NOTE_WHAT} ${NOTE_IDENTIFIER} THE LOAD-BEARING MESSAGE OF THIS SCREEN: it must rule out the ` +
      'connection explicitly and must not suggest retrying. If it reads like "check your connection" in ' +
      'your language, a technician will go and stand in a car park hunting for signal over a problem in ' +
      'our backlog. It must share no sentence with the offline or network-error messages.',
  ),
  def(
    NOTICE_KEYS.serverErrorTitle,
    'The server had a problem',
    'notice-title',
  ),
  def(
    NOTICE_KEYS.serverErrorBody,
    'The server could not {what} and did not say why. This one is worth trying again in a few ' +
      'minutes.',
    'notice-body',
    `${NOTE_WHAT} This is the ONE outcome where retrying is the right advice; keep that advice explicit.`,
  ),
  def(NOTICE_KEYS.notFoundTitle, 'No record like that', 'notice-title'),
  def(
    NOTICE_KEYS.notFoundBody,
    'Either there is no such record, or it is not assigned to your company. The server answers ' +
      'the same way for both on purpose, so check the number on your dispatch first.',
    'notice-body',
    'The ambiguity is a deliberate authorization control, not a shortcoming. The translation must keep ' +
      'both possibilities open rather than picking the friendlier one.',
  ),
  def(NOTICE_KEYS.rejectedTitle, 'The server refused the request', 'notice-title'),
  def(
    NOTICE_KEYS.rejectedBody,
    'It could not {what} because the request was not acceptable. Waiting will not change that. ' +
      'The code below is what to quote.',
    'notice-body',
    `${NOTE_WHAT} "Waiting will not change that" is the operative half — this one is not retryable.`,
  ),
  def(NOTICE_KEYS.conflictTitle, 'The server has a conflicting copy', 'notice-title'),
  def(
    NOTICE_KEYS.conflictBody,
    'This should not be able to happen — it means two submissions claimed the same key. Nothing ' +
      'has been lost. Please report it.',
    'notice-body',
    '"Nothing has been lost" must survive: the technician\'s first fear on seeing this is that their ' +
      'work is gone.',
  ),
  def(NOTICE_KEYS.unauthenticatedTitle, 'Not signed in', 'notice-title'),
  def(
    NOTICE_KEYS.unauthenticatedBody,
    'You need to sign in again before the app can {what}. Your saved work stays where it is.',
    'notice-body',
    `${NOTE_WHAT} A session that expired. Distinct from ui.api.auth-not-configured.*, where there is no ` +
      'session to expire because sign-in does not exist yet.',
  ),
  def(
    NOTICE_KEYS.authNotConfiguredTitle,
    'Sign-in is not built yet, so nothing was sent',
    'notice-title',
    'NOT a signed-out state. Nothing was sent at all. A 401-shaped translation sends someone looking ' +
      'for a password problem they do not have.',
  ),
  def(
    NOTICE_KEYS.authNotConfiguredBody,
    'The app did not try to {what}, because there is no way to sign in yet. This is an unbuilt ' +
      'feature, not a signed-out state and not a connection problem. Everything you submit is still ' +
      'saved on this phone.',
    'notice-body',
    `${NOTE_WHAT} Rules out BOTH a sign-in problem and a connection problem; keep both denials.`,
  ),
  def(
    NOTICE_KEYS.quoteLabel,
    'Quote this if you report it',
    'notice-label',
    'Labels the one line a technician is asked to read aloud over the phone. The line itself (a blocker ' +
      'name, an HTTP status, a request id) is never translated.',
  ),
];

/* ─────────────────────────────────────────────────────────── derived lookups ───────────────── */

export interface UiEntry {
  readonly key: UiKey;
  readonly pattern: MessagePattern;
  /** `canonicalSource(pattern)` — the exact string the hash is over. */
  readonly source: string;
  readonly hash: string;
  readonly role: UiRole;
  readonly namespace: UiNamespace;
  /** Placeholder names this message needs. A call site must supply all of them. */
  readonly placeholders: readonly string[];
  /** Plural categories the English declares. `[]` for a plain string. */
  readonly categories: readonly PluralCategory[];
  readonly notes?: string;
}

/** `ui.validate.x.y` → `validate`. Throws only for a key that is not `ui.*`, which the lint catches. */
export function uiNamespace(key: string): UiNamespace {
  const part = key.split('.')[1];
  if (part === 'validate' || part === 'storage' || part === 'api') return part;
  throw new Error(`"${key}" is not a recognised ui.* namespace (expected ui.validate.*, ui.storage.* or ui.api.*)`);
}

function toEntry(spec: UiStringDef): UiEntry {
  const source = canonicalSource(spec.pattern);
  return {
    key: spec.key,
    pattern: spec.pattern,
    source,
    hash: hashSource(source),
    role: spec.role,
    namespace: uiNamespace(spec.key),
    placeholders: placeholdersIn(spec.pattern),
    categories: declaredCategories(spec.pattern),
    ...(spec.notes !== undefined ? { notes: spec.notes } : {}),
  };
}

export const UI_ENTRIES: readonly UiEntry[] = UI_STRINGS.map(toEntry);

/**
 * Keys declared more than once. **Asserted empty.**
 *
 * This is the guard derived keys do not need. A duplicate would mean the second declaration silently
 * wins and one message renders the other's text — the mis-attached-label defect, reintroduced by the
 * one place this design had to declare keys by hand.
 */
export const UI_DUPLICATE_KEYS: readonly string[] = (() => {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const spec of UI_STRINGS) {
    if (seen.has(spec.key)) dupes.add(spec.key);
    seen.add(spec.key);
  }
  return [...dupes];
})();

const UI_BY_KEY: ReadonlyMap<string, UiEntry> = new Map(UI_ENTRIES.map((e) => [e.key, e]));

export function uiEntry(key: string): UiEntry | undefined {
  return UI_BY_KEY.get(key);
}

export function uiKeys(): ReadonlySet<string> {
  return new Set(UI_BY_KEY.keys());
}

/** `true` for any key in the `ui.*` namespace, declared or not. Cheap enough for a render path. */
export function isUiKey(key: string): boolean {
  return key.startsWith('ui.');
}

/**
 * The English lookup, in the shape `resolve.ts` wants.
 *
 * This is the function that makes "the English fallback cannot be lost" structural rather than
 * hopeful: there is one code path to a `ui.*` string and it reads the English on the way past.
 */
export const uiSourceLookup = (key: string): SourceRecord | undefined => {
  const entry = UI_BY_KEY.get(key);
  if (entry === undefined) return undefined;
  return { pattern: entry.pattern, hash: entry.hash };
};

/* ------------------------------------------------------------------- anti-vacuity floors ---- */

/**
 * ANTI-VACUITY FLOOR. **Measured 2026-09-03: `UI_ENTRIES.length === 40`.**
 *
 * Set to 38, the sum of the per-namespace floors below. The same rule as `extract.ts`: if a legitimate
 * change pushes the real count under this, question the change before lowering the number. A floor that
 * is edited whenever it fails is a comment.
 */
export const MIN_EXPECTED_UI_ENTRIES = 38;

/**
 * Per-namespace floors, because a total-only floor is satisfiable by one namespace.
 *
 * `api` alone is 21 of the 40, so dropping every validation message — the *blocking* ones, the whole
 * reason this work was prioritised — would still leave 25 and clear a floor of 36 minus slack. These
 * are the numbers that catch that.
 *
 * Measured 2026-09-03: validate 15, storage 4, api 21. `validate` and `storage` are set at the measured
 * value with NO slack, deliberately: losing a blocking validation message or one of the four storage
 * verdicts is precisely the regression worth a red build, and neither set has any reason to shrink.
 */
export const MIN_EXPECTED_UI_PER_NAMESPACE: Readonly<Record<UiNamespace, number>> = {
  validate: 15,
  storage: 4,
  api: 19,
};

/* --------------------------------------------------------------------------- statistics ---- */

export function uiStats(entries: readonly UiEntry[] = UI_ENTRIES): {
  total: number;
  byNamespace: Record<string, number>;
  byRole: Record<string, number>;
  plural: number;
  withPlaceholders: number;
  characters: number;
} {
  const byNamespace: Record<string, number> = {};
  const byRole: Record<string, number> = {};
  let plural = 0;
  let withPlaceholders = 0;
  let characters = 0;

  for (const entry of entries) {
    byNamespace[entry.namespace] = (byNamespace[entry.namespace] ?? 0) + 1;
    byRole[entry.role] = (byRole[entry.role] ?? 0) + 1;
    if (isPluralPattern(entry.pattern)) plural += 1;
    if (entry.placeholders.length > 0) withPlaceholders += 1;
    characters += entry.source.length;
  }

  return { total: entries.length, byNamespace, byRole, plural, withPlaceholders, characters };
}

export interface UiHandoffEntry {
  readonly key: string;
  /** For a plural, every declared form labelled by category. */
  readonly source: string;
  readonly sourceHash: string;
  readonly role: UiRole;
  readonly placeholders: readonly string[];
  readonly pluralCategories: readonly PluralCategory[];
  readonly notes?: string;
}

/**
 * The component-string half of the vendor handoff, alongside `buildHandoff()` for the schema strings.
 *
 * ⚠️ **EMITS ENGLISH ONLY. NO TRANSLATION HAS BEEN PRODUCED BY THIS WORK.** `sourceHash` travels with
 * each string so a returned catalogue can be checked for staleness on arrival rather than in the field,
 * and `pluralCategories` tells the vendor which forms the English declares — they are expected to
 * return the categories *their* language uses, which is not the same set.
 */
export function buildUiHandoff(entries: readonly UiEntry[] = UI_ENTRIES): readonly UiHandoffEntry[] {
  return entries.map((entry) => ({
    key: entry.key,
    source: entry.source,
    sourceHash: entry.hash,
    role: entry.role,
    placeholders: entry.placeholders,
    pluralCategories: entry.categories,
    ...(entry.notes !== undefined ? { notes: entry.notes } : {}),
  }));
}
